import Vue, { CreateElement } from '@src/vue'
import { Component, Watch } from 'vue-property-decorator'
import camelcaseKeys from 'camelcase-keys'

import ApiClient from '@src/lib/api_client'
import Member, { MemberPermissions } from '@src/data/member'
import { Button } from '@src/elements/button/button'
import { Tabs, Tab } from '@src/components/tabs/tabs'
import { ActionBar } from '@src/components/action-bar/action-bar'
import { Breadcrumbs } from '@src/elements/breadcrumbs/breadcrumbs'
import { FlagsCell } from './components/list/flags-cell'
import { Loader } from '@src/components/loading-screen/loader/loader'
import { API } from '@src/lib/api_path'
import { MemberPermissionsSettings } from '@src/routes/members/components/active-trainings-workouts/permissions'
import { snakecaseKeys } from '@src/util/functions'
import EventBus from '@src/lib/event_bus'
import { TermsAndConditions } from '@src/routes/members/terms-conditions'
import MemberOnboardingStatus from '@src/routes/members/components/onboarding/onboarding-status'
import Checkup from '@src/data/checkup'
import { MemberService } from '@src/service/member'
import { Subtitle2 } from '@src/elements/typography/typography'

import './details.sass'

enum ContentType {
  STATUS = 'status',
  ACTIVITIES = 'member_activities',
  TRAINING = 'training',
  ACCOUNT = 'account',
  TASKS = 'tasks',
  CHECKUPS = 'checkups',
  NOTES = 'notes',
  HEALTH = 'health',
  BODY_MEASUREMENTS = 'body-measurements',
  RESULTS_HISTORY = 'history'
}

type CheckupResponse = { checkup: Checkup }

@Component({
  computed: {
    member () {
      return this.$store.state.selectedMember
    }
  }
})
export class MemberDetails extends Vue {

  private member: Member
  private checkup: Checkup
  private showOnboardingStatus: boolean = false

  private loadingMember: boolean = true
  private loadingCheckup: boolean = true
  private apiClient: ApiClient = new ApiClient()

  private arePermissionsVisible: boolean = false

  constructor () {
    super()
  }

  selectTab (type: ContentType | string) {
    this.$router.push(`/trainers/platform/members/${this.member.id}/${type}`)

    const tabBody = this.$refs.tabBody as Element
    tabBody.scrollTop = 0
    // update the latest data of the member on each tab change
    return this.loadMember()
  }

  isTabSelected (type: ContentType) {
    return this.$route.path.indexOf(type.split('/')[0]) >= 0
  }

  showHidePermissions () {
    this.arePermissionsVisible = !this.arePermissionsVisible
  }

  onPermissionsUpdated (permissions?: MemberPermissions) {
    this.arePermissionsVisible = false
    if (permissions) {
      return this.apiClient.put(API.TRAINERS_API.MEMBER_PERMISSIONS.replace('{memberId}', this.member.id.toString()), { member_permissions: snakecaseKeys(permissions) })
        .catch(error => console.log('cannot save member permission', error))
    }
  }

  goToUsers () {
    this.$router.push({ path: '/trainers/platform/members/' })
  }

  render (h: CreateElement) {
    if (this.loadingMember || this.loadingCheckup) {
      return <Loader />
    }

    if (this.member.termsReacceptanceRequired) {
      return <div class='member-details'>
        <ActionBar>
          <Breadcrumbs clickAction={this.goToUsers} crumbs={[
            <div>{this.$t('USERS')}</div>
          ]} />
          <div class='full-name-wrapper'>
            <Subtitle2>{this.member.fullName}</Subtitle2>
          </div>
          <FlagsCell item={this.member} showInBreadcrumb={true} />
        </ActionBar>

        <TermsAndConditions />
      </div>
    }

    return <div class='member-details'>
      <ActionBar>
        <Breadcrumbs clickAction={this.goToUsers} crumbs={[
          <div>{this.$t('USERS')}</div>
        ]} />
        <div class='full-name-wrapper'>
          <Subtitle2>{this.member.fullName}</Subtitle2>
        </div>
        <FlagsCell item={this.member} showInBreadcrumb={true} />

        <div class='absolute-btns'>
          {this.isTabSelected(ContentType.TRAINING) ?
            <Button align='right' type='elevated' label={this.$t('PAGE.MEMBERS.TRAINING.PERMISSIONS')} click={this.showHidePermissions} /> : null}
          {this.arePermissionsVisible ? <MemberPermissionsSettings permissions={this.member.memberPermissions} closeCallback={this.onPermissionsUpdated} /> : null}
        </div>
      </ActionBar>

      {this.showOnboardingStatus ?
        <MemberOnboardingStatus />

        : <div class='component-tabs-wrapper'><Tabs>
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.STATUS')} selected={this.isTabSelected(ContentType.STATUS)}
               selectTab={() => this.selectTab(ContentType.STATUS)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.TRAINING')} selected={this.isTabSelected(ContentType.TRAINING)}
               selectTab={() => this.selectTab(ContentType.TRAINING)} />
          <Tab label={this.$t('HISTORY')} selected={this.isTabSelected(ContentType.RESULTS_HISTORY)}
               selectTab={() => this.selectTab(ContentType.RESULTS_HISTORY)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.ACTIVITIES')} selected={this.isTabSelected(ContentType.ACTIVITIES)}
               selectTab={() => this.selectTab(ContentType.ACTIVITIES)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.HEALTH')} selected={this.isTabSelected(ContentType.HEALTH)}
               selectTab={() => this.selectTab(ContentType.HEALTH)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.BODY_MEASUREMENTS')} selected={this.isTabSelected(ContentType.BODY_MEASUREMENTS)}
               selectTab={() => this.selectTab(ContentType.BODY_MEASUREMENTS)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.CHECKUPS')} selected={this.isTabSelected(ContentType.CHECKUPS)}
               selectTab={() => this.selectTab(ContentType.CHECKUPS)} />
          {/*<Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.TASKS')} selected={this.isTabSelected(ContentType.TASKS)}*/}
          {/*selectTab={() => this.selectTab(ContentType.TASKS)} />*/}
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.NOTES')} selected={this.isTabSelected(ContentType.NOTES)}
               selectTab={() => this.selectTab(ContentType.NOTES)} />
          <Tab label={this.$t('PAGE.MEMBERS.DETAIL.TABS.ACCOUNT')} selected={this.isTabSelected(ContentType.ACCOUNT)}
               selectTab={() => this.selectTab(ContentType.ACCOUNT)} />
        </Tabs>

          <div class='component-tabs-body' ref='tabBody'>
            <router-view />
          </div>
        </div>
      }
    </div>
  }

  beforeMount () {
    EventBus.on('reloadSelectedMember', this.loadMember)

    return this.loadMember()
  }

  beforeDestroy () {
    EventBus.off('reloadSelectedMember', this.loadMember)
  }

  @Watch('member')
  loadOnboardingCompletion (): any {
    if (this.member) {
      // registered members do not need to go through the onboarding
      if (this.member.registeredAt) {
        this.showOnboardingStatus = false
        this.loadingCheckup = false
        return
      }

      // if there is no initial checkup id, than the onboarding process has not been started
      if (!this.member.initialCheckupId) {
        const checkup = new Checkup()
        checkup.startOnboarding()
        this.$store.dispatch('setCheckup', checkup)
        this.showOnboardingStatus = true
        this.loadingCheckup = false
        return
      }

      return this.getOnboardingCheckup()
    }
  }

  @Watch('checkup')
  onCheckupUpdated () {
    if (this.checkup.isComplete()) {
      this.showOnboardingStatus = false
    }
  }

  private loadMember () {
    if (this.$route.params.id) {
      return new MemberService().getMember(this.$route.params.id)
        .then((member) => this.$store.dispatch('setSelectedMember', member))
        .finally(() => this.loadingMember = false)
    }
  }

  private getOnboardingCheckup () {
    // get the initial checkup and see if all steps have been completed
    return this.apiClient.get(`${API.TRAINERS_API.CHECKUPS.replace('{memberId}', this.$route.params.id)}/${this.member.initialCheckupId}`)
      .then((response: CheckupResponse) => {
        let checkup = new Checkup(camelcaseKeys(response.checkup, { deep: true }))
        checkup.startOnboarding()
        if (!checkup.isComplete()) {
          this.showOnboardingStatus = true
          this.$store.dispatch('setCheckup', checkup)
        }
      })
      .finally(() => this.loadingCheckup = false)
  }

}
